import React, { useState } from 'react';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import PhoneInput from '../../components/PhoneInput';
import { useAuth } from '../../context/AuthContext';

const STATES = ['QLD', 'NSW', 'VIC', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
const inputClass = 'box-border block w-full min-w-0   rounded-lg border-0 bg-[#06110c] px-3.5 py-3 text-sm font-medium text-white outline-none ring-1 ring-inset ring-white/10 transition placeholder:text-white/25 hover:ring-white/20 focus:ring-2 focus:ring-[#20c77a]';
const labelClass = 'mb-1.5 block text-[11px] font-semibold text-white/65';

const ClientRegister = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    suburb: '',
    state: 'QLD',
    postcode: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) return setError('Passwords do not match.');
    if (form.password.length < 6) return setError('Password must be at least 6 characters.');
    if (!/^\d{4}$/.test(form.postcode)) return setError('Please enter a valid 4-digit Australian postcode.');

    setLoading(true);
    try {
      const user = await register({
        name: form.name,
        email: form.email,
        phone: form.phone,
        password: form.password,
        role: 'client',
        suburb: form.suburb,
        state: form.state,
        postcode: form.postcode,
      });

      if (user) navigate('/client/dashboard');
      else setError('Registration failed. Please check your details and try again.');
    } catch (err) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative flex min-h-screen w-full items-center justify-center overflow-x-hidden bg-[#020906] px-4 py-8 font-dm text-white sm:px-6 sm:py-10 lg:px-8">
      <title>Create a Client Account | GIGGS</title>
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(25,177,105,0.14),transparent_34%),linear-gradient(rgba(255,255,255,0.018)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.018)_1px,transparent_1px)] bg-[size:auto,22px_22px,22px_22px]" />

      <section className="relative z-10 w-full max-w-[760px] rounded-2xl border border-[#1c4b36]/70 bg-[#04100b]/95 p-5 shadow-[0_25px_90px_rgba(0,0,0,0.38)] backdrop-blur-sm sm:p-7 lg:p-8">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center gap-2 text-xs font-semibold text-white/40 no-underline transition hover:text-[#46dc96] sm:text-sm"><ArrowLeft size={16} /> Back to home</Link>
          <Link to="/" aria-label="Giggs home"><img src="/images/logo-white.png" alt="Giggs" className="h-8 w-auto" /></Link>
        </div>

        <div className="mt-7 text-center">
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#42d993]">Client registration</span>
          <h1 className="mb-0 mt-2 font-nunito text-[clamp(1.8rem,6vw,2.5rem)] font-bold tracking-[-0.02em] text-white">Create your Giggs account</h1>
          <p className="mx-auto mb-0 mt-2 max-w-xl text-sm leading-6 text-white/35">Create one account to manage your bookings and return to your dashboard anytime.</p>
        </div>

        {error && <div className="mt-5 rounded-lg border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm font-medium text-red-200">{error}</div>}

        <form onSubmit={handleSubmit} className="mt-6 grid min-w-0 gap-4 sm:grid-cols-2">
          <label className="block min-w-0 sm:col-span-2"><span className={labelClass}>Full name <span className="text-[#35dc8b]">*</span></span><input className={inputClass} name="name" value={form.name} onChange={handleChange} placeholder="Enter your full name" autoComplete="name" required /></label>
          <label className="block min-w-0"><span className={labelClass}>Email <span className="text-[#35dc8b]">*</span></span><input className={inputClass} type="email" name="email" value={form.email} onChange={handleChange} placeholder="Enter your email" autoComplete="email" required /></label>
          <label className="block min-w-0"><span className={labelClass}>Australian mobile <span className="text-[#35dc8b]">*</span></span><PhoneInput className={inputClass} value={form.phone} onChange={(phone) => setForm((current) => ({ ...current, phone }))} required /></label>

          <label className="block min-w-0">
            <span className={labelClass}>Password <span className="text-[#35dc8b]">*</span></span>
            <div className="relative">
              <input className={`${inputClass} pr-11`} type={showPassword ? 'text' : 'password'} name="password" value={form.password} onChange={handleChange} placeholder="Minimum 6 characters" autoComplete="new-password" required />
              <button type="button" onClick={() => setShowPassword((visible) => !visible)} className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md border-0 bg-transparent text-white/25 transition hover:bg-white/[0.05] hover:text-white/70" aria-label={showPassword ? 'Hide password' : 'Show password'}>{showPassword ? <EyeOff size={17} /> : <Eye size={17} />}</button>
            </div>
          </label>
          <label className="block min-w-0"><span className={labelClass}>Confirm password <span className="text-[#35dc8b]">*</span></span><input className={inputClass} type={showPassword ? 'text' : 'password'} name="confirmPassword" value={form.confirmPassword} onChange={handleChange} placeholder="Re-enter password" autoComplete="new-password" required /></label>

          <label className="block min-w-0 sm:col-span-2"><span className={labelClass}>Suburb <span className="text-[#35dc8b]">*</span></span><input className={inputClass} name="suburb" value={form.suburb} onChange={handleChange} placeholder="Enter your suburb" autoComplete="address-level2" required /></label>
          <label className="block min-w-0"><span className={labelClass}>State <span className="text-[#35dc8b]">*</span></span><select className={`${inputClass} cursor-pointer`} name="state" value={form.state} onChange={handleChange}>{STATES.map((state) => <option className="bg-[#06110c]" key={state} value={state}>{state}</option>)}</select></label>
          <label className="block min-w-0"><span className={labelClass}>Postcode <span className="text-[#35dc8b]">*</span></span><input className={inputClass} name="postcode" value={form.postcode} onChange={handleChange} maxLength={4} inputMode="numeric" placeholder="4000" autoComplete="postal-code" required /></label>

          <button type="submit" disabled={loading} className="mt-2 inline-flex min-h-11 items-center justify-center rounded-lg border border-[#20c77a] bg-[#12ad68] px-5 text-sm font-bold text-[#02140b] shadow-[0_10px_30px_rgba(18,173,104,0.16)] transition hover:-translate-y-0.5 hover:bg-[#25d080] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 sm:col-span-2">{loading ? 'Creating account…' : 'Create client account'}</button>
        </form>

        <div className="mt-6 border-t border-white/[0.07] pt-5 text-center text-xs text-white/35">
          Already registered? <Link to="/login" className="font-semibold text-[#42d993] no-underline hover:text-[#71efb2]">Sign in</Link>
          <span className="mx-2 text-white/15">•</span>
          Want to offer services? <Link to="/provider-register" className="font-semibold text-[#42d993] no-underline hover:text-[#71efb2]">Become a Gigger</Link>
        </div>
      </section>
    </main>
  );
};

export default ClientRegister;
